import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  TextField,
  InputAdornment,
  Button,
  useTheme,
  Alert,
  Snackbar,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  TableChart as TableChartIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import UserMode from "./UserMode";
import CompanyMode from "./CompanyMode";
import PermissionTableView from "./PermissionTableView";
import { RestrictedChip } from "../../CompanyList";

const ManagerPanel = ({
  user_role,
  user_type,
  user_id,
  isSubscribed,
  companies,
  onGetPermission,
  onAssignPermission,
  onAssignUsersToCompany,
  onGetCompanyUserPermissionList,
  subUsers,
  subsidiaries,
  bulkPermissionModalOpen,
  setBulkPermissionModalOpen,
}) => {
  const theme = useTheme();
  const [userSearch, setUserSearch] = useState("");
  const [userPermissions, setUserPermissions] = useState([]);
  const [companyPermissions, setCompanyPermissions] = useState([]);
  const [loadingPermission, setLoadingPermission] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [editingPermission, setEditingPermission] = useState(null);
  const [savingPermission, setSavingPermission] = useState(false);
  const [importFromUserId, setImportFromUserId] = useState("");
  const [importingPermission, setImportingPermission] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [viewMode, setViewMode] = useState("user"); // 'user' | 'company'
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [loadingCompanyPermissions, setLoadingCompanyPermissions] =
    useState(false);
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [permissionBlockedMessage, setPermissionBlockedMessage] = useState("");
  const [allUsers, setAllUsers] = useState(
    [...(subUsers || []), ...(subsidiaries || [])].filter(Boolean)
  );

  const safeLower = (v) => String(v ?? "").toLowerCase();
  const getUserStatus = (user) =>
    safeLower(
      user?.status ||
        user?.user_status ||
        user?.auth_status ||
        user?.user_auth?.status
    );
  const isInactiveStatus = (status) =>
    safeLower(status) === "inactive" || safeLower(status) === "pending";
  const findUserById = (userId) => allUsers.find((u) => u?.u_id === userId);
  const isUserInactive = (userOrId) => {
    if (!userOrId) return false;
    const user =
      typeof userOrId === "string" ? findUserById(userOrId) : userOrId;
    return isInactiveStatus(getUserStatus(user));
  };
  const showInactivePermissionBlock = () => {
    setPermissionBlockedMessage(
      "Inactive users cannot have their permissions changed."
    );
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (
          selectedUserId &&
          editingPermission >= 0 &&
          userPermissions[editingPermission] &&
          !savingPermission
        ) {
          const perm = userPermissions[editingPermission];
          const hasPartial = perm.is_partial_access;
          const yearsCount = (perm.allowed_yoa || []).length;
          if (perm.role === "none" || !hasPartial || yearsCount > 0) {
            handleSavePermission(perm);
          }
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedUserId, editingPermission, userPermissions, savingPermission]);

  const normalizePermissionResponse = (permissionList) => {
    if (Array.isArray(permissionList)) {
      return permissionList;
    }
    if (permissionList?.data && Array.isArray(permissionList.data)) {
      return permissionList.data;
    }
    if (
      permissionList?.permissions &&
      Array.isArray(permissionList.permissions)
    ) {
      return permissionList.permissions;
    }
    return [];
  };

  useEffect(() => {
    setAllUsers([...(subUsers || []), ...(subsidiaries || [])].filter(Boolean));
  }, [subUsers, subsidiaries]);

  const filteredUsers = allUsers.filter((user) => {
    const q = safeLower(userSearch);
    return (
      safeLower(user?.name).includes(q) || safeLower(user?.email).includes(q)
    );
  });

  const filteredCompanies = (companies || [])
    .filter(Boolean)
    .filter((company) => {
      const q = safeLower(userSearch);
      if (!q) return true;
      return (
        safeLower(company?.name).includes(q) ||
        safeLower(company?.company_name).includes(q) ||
        safeLower(company?.uen).includes(q) ||
        safeLower(company?.company_uen).includes(q)
      );
    });

  const getAvailableYears = (permission) => {
    const years = permission?.tax_computation_years;
    if (Array.isArray(years) && years.length > 0)
      return [...years].sort((a, b) => a - b);
    return [];
  };

  const handleSelectAllYears = (permissionIndex, selectAll) => {
    const permission = userPermissions[permissionIndex];
    const availableYears = getAvailableYears(permission);
    const updatedYears = selectAll ? availableYears : [];
    handleUpdatePermission(permissionIndex, "allowed_yoa", updatedYears);
  };

  const loadCompanyPermissionIndex = async () => {
    if (!onGetCompanyUserPermissionList) return;

    try {
      setLoadingCompanyPermissions(true);

      const response = await onGetCompanyUserPermissionList();
      const list = normalizePermissionResponse(response);

      const index = {};

      // 1️⃣ Initialize companies first
      companies.forEach((company) => {
        index[company.c_id] = {
          company,
          users: {},
          userList: [],
        };
      });

      // 2️⃣ Fill user permissions
      list.forEach((item) => {
        if (item.u_id === user_id || item.user_type === "admin") return;
        const cId = item.company_id || item.c_id;
        const uId = item.u_id;
        if (!cId || !uId) return;

        // ensure company bucket exists
        if (!index[cId]) {
          index[cId] = {
            company: companies.find((c) => c.c_id === cId) || null,
            users: {},
            userList: [],
          };
        }

        const userRecord = findUserById(uId);
        const enrichedItem = {
          ...item,
          status: item.status || item.user_status || userRecord?.status,
          user_auth: item.user_auth || userRecord?.user_auth,
        };

        index[cId].users[uId] = enrichedItem;
        index[cId].userList.push(enrichedItem);
      });

      setCompanyPermissions(index);
    } catch (error) {
      console.error("Failed to load company user permissions:", error);
      setCompanyPermissions({});
    } finally {
      setLoadingCompanyPermissions(false);
    }
  };

  const handleGetPermission = async (userId) => {
    try {
      setLoadingPermission(true);
      setSelectedUserId(userId);
      setUserPermissions([]);
      const permissionList = await onGetPermission(userId);
      const normalized = normalizePermissionResponse(permissionList);
      setUserPermissions(normalized);
    } catch (error) {
      console.error("Failed to fetch permissions:", error);
      setUserPermissions([]);
    } finally {
      setLoadingPermission(false);
    }
  };

  const handleUpdatePermission = (permissionIndex, field, value) => {
    if (isUserInactive(selectedUserId)) {
      showInactivePermissionBlock();
      return;
    }
    setUserPermissions((prev) => {
      const updated = [...prev];
      updated[permissionIndex] = {
        ...updated[permissionIndex],
        [field]: value,
      };
      return updated;
    });
  };

  const handleSaveAllPermissions = async (latestPermissions) => {
    if (isUserInactive(selectedUserId)) {
      showInactivePermissionBlock();
      return;
    }
    try {
      setSavingPermission(true);

      if (onAssignPermission) {
        await onAssignPermission({
          target_u_id: selectedUserId,
          items: latestPermissions,
        });
        if (selectedUserId) {
          await handleGetPermission(selectedUserId);
        }
      }
      setEditingPermission(null);
      setSaveSuccess(true);
    } catch (error) {
      console.error("Failed to save all permissions:", error);
      throw error;
    } finally {
      setSavingPermission(false);
    }
  };

  const handleSavePermission = async (permission) => {
    if (isUserInactive(selectedUserId)) {
      showInactivePermissionBlock();
      return;
    }
    try {
      setSavingPermission(true);
      if (onAssignPermission) {
        await onAssignPermission({
          target_u_id: selectedUserId,
          items: [permission],
        });
        if (selectedUserId) {
          await handleGetPermission(selectedUserId);
        }
      }
      setEditingPermission(null);
      setSaveSuccess(true);
    } catch (error) {
      console.error("Failed to save permission:", error);
      throw error;
    } finally {
      setSavingPermission(false);
    }
  };

  const handleToggleYear = (permissionIndex, year) => {
    if (isUserInactive(selectedUserId)) {
      showInactivePermissionBlock();
      return;
    }
    const permission = userPermissions[permissionIndex];
    const currentYears = permission.allowed_yoa || [];
    const updatedYears = currentYears.includes(year)
      ? currentYears.filter((y) => y !== year)
      : [...currentYears, year].sort((a, b) => a - b);

    handleUpdatePermission(permissionIndex, "allowed_yoa", updatedYears);
  };

  const handleImportPermissions = async () => {
    if (!selectedUserId || !importFromUserId || !onGetPermission) return;
    if (isUserInactive(selectedUserId)) {
      showInactivePermissionBlock();
      return;
    }

    try {
      setImportingPermission(true);
      // Fetch permissions from the source user
      const sourceResponse = await onGetPermission(importFromUserId);
      const sourcePermissions = normalizePermissionResponse(sourceResponse);

      if (!sourcePermissions.length || !onAssignPermission) {
        setImportingPermission(false);
        return;
      }

      // Apply the same permissions to the selected user
      await onAssignPermission({
        target_u_id: selectedUserId,
        items: sourcePermissions,
      });

      // Refresh currently viewed permissions
      await handleGetPermission(selectedUserId);
    } catch (error) {
      console.error("Failed to import permissions:", error);
    } finally {
      setImportingPermission(false);
    }
  };

  const handleClose = () => {
    setBulkPermissionModalOpen(false);
    setUserPermissions([]);
    setSelectedUserId(null);
    setEditingPermission(null);
    setSelectedCompanyId(null);
  };

  const handleUserClick = (user) => {
    if (selectedUserId !== user.u_id) {
      setSelectedUserId(user.u_id);
      handleGetPermission(user.u_id);
      setEditingPermission(null);
    }
  };

  const handleCompanyClick = (company) => {
    if (!company?.c_id) return;
    if (selectedCompanyId !== company.c_id) {
      setSelectedCompanyId(company.c_id);
      setSelectedUserId(null);
      setEditingPermission(null);
    }
  };

  const handleSaveCompanyUserRoles = async (cId, items) => {
    if (!cId || !items?.length || !onAssignUsersToCompany) return;
    if (items.some((item) => isUserInactive(item.target_u_id))) {
      showInactivePermissionBlock();
      return;
    }

    const payload = items.map((item) => ({
      target_u_id: item.target_u_id,
      role: item.role || "none",
      is_partial_access: item.is_partial_access ?? false,
      allowed_yoa: item.allowed_yoa ?? [],
    }));

    if (payload.length === 0) return;

    try {
      setCompanyPermissions((prev) => {
        const copy = { ...prev };
        const bucket = copy[cId] || { company: null, users: {}, userList: [] };
        const newUsers = { ...(bucket.users || {}) };
        payload.forEach((item) => {
          const existing = newUsers[item.target_u_id] || {};
          newUsers[item.target_u_id] = {
            ...existing,
            c_id: cId,
            u_id: item.target_u_id,
            role: item.role,
            is_partial_access: item.is_partial_access,
            allowed_yoa: item.allowed_yoa,
          };
        });
        copy[cId] = {
          ...bucket,
          users: newUsers,
          userList: Object.values(newUsers),
        };
        return copy;
      });

      await onAssignUsersToCompany({
        c_id: cId,
        items: payload,
      });

      await loadCompanyPermissionIndex();
      setSaveSuccess(true);
    } catch (error) {
      console.error("Failed to update company permissions:", error);
    }
  };

  useEffect(() => {
    if (!bulkPermissionModalOpen) return;
    if (viewMode === "company") {
      loadCompanyPermissionIndex();
    } else if (viewMode === "user" && selectedUserId) {
      handleGetPermission(selectedUserId);
    }
  }, [viewMode, bulkPermissionModalOpen]);

  if (user_role !== "admin") return null;
  if (!isSubscribed) {
    return (
      <Modal
        open={bulkPermissionModalOpen}
        onClose={(_, reason) => {
          if (reason !== "backdropClick") handleClose();
        }}
        sx={{ zIndex: 1300 }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "98%",
            maxWidth: 700,
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "background.paper",
            }}
          >
            <Typography variant='h6' fontWeight={700} color='text.primary'>
              Permission Manager
            </Typography>
            <IconButton onClick={handleClose} size='small' sx={{ mr: 1 }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3 }}>
            <Alert severity='warning' sx={{ mb: 2 }}>
              No access. This feature is available for Tax Professional plan
              only.
            </Alert>
            <Typography variant='body2' color='text.secondary'>
              Please upgrade your plan to manage sub-user permissions.
            </Typography>
          </Box>

          <Box
            sx={{
              px: 3,
              py: 2,
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
              justifyContent: "flex-end",
              bgcolor: "background.paper",
            }}
          >
            <Button variant='contained' onClick={handleClose}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        open={bulkPermissionModalOpen}
        onClose={(_, reason) => {
          if (reason !== "backdropClick") handleClose();
        }}
        sx={{ zIndex: 1300 }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "98%",
            maxWidth: 1600,
            height: "92vh",
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: 24,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* --- Header: 极简头部 --- */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "background.paper",
            }}
          >
            <Box>
              <Typography variant='h6' fontWeight={700} color='text.primary'>
                Permission Manager
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {viewMode === "user"
                  ? "Manage access levels and year restrictions per member"
                  : "Assign roles quickly for each company"}
              </Typography>
            </Box>
            <IconButton onClick={handleClose} size='small' sx={{ mr: 1 }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* --- Main Content: 左右分栏 --- */}
          <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
            {/* LEFT: 用户列表 (Navigation) - 窄而紧凑 */}
            <Box
              sx={{
                width: 300,
                borderRight: 1,
                borderColor: "divider",
                display: "flex",
                flexDirection: "column",
                bgcolor: "grey.50",
              }}
            >
              {/* Mode Toggle + Search */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: 1,
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Button
                    size='small'
                    variant={viewMode === "user" ? "contained" : "outlined"}
                    onClick={() => {
                      setViewMode("user");
                      setSelectedCompanyId(null);
                    }}
                    sx={{ textTransform: "none", flex: 1 }}
                  >
                    By User
                  </Button>
                  <Button
                    size='small'
                    variant={viewMode === "company" ? "contained" : "outlined"}
                    onClick={() => {
                      setViewMode("company");
                      setSelectedUserId(null);
                    }}
                    sx={{ textTransform: "none", flex: 1 }}
                  >
                    By Company
                  </Button>
                </Box>

                <TextField
                  fullWidth
                  size='small'
                  placeholder={
                    viewMode === "user" ? "Find member..." : "Find company..."
                  }
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon fontSize='small' color='action' />
                      </InputAdornment>
                    ),
                    sx: { bgcolor: "grey.100", borderRadius: 1 },
                  }}
                />
              </Box>

              {/* List */}
              <List sx={{ p: 1, overflow: "auto", flex: 1, minHeight: 0 }}>
                {viewMode === "user"
                  ? filteredUsers.map((user) => {
                      const isSelected = selectedUserId === user.u_id;
                      const hasPerms = user.permissions?.length > 0;

                      return (
                        <ListItem
                          key={user.u_id}
                          button
                          selected={isSelected}
                          onClick={() => handleUserClick(user)}
                          sx={{
                            borderRadius: 2,
                            mb: 0.5,
                            px: 2,
                            py: 1.5,
                            "&.Mui-selected": {
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              "&:hover": {
                                bgcolor: alpha(
                                  theme.palette.primary.main,
                                  0.12
                                ),
                              },
                              "& .MuiTypography-root": {
                                color: "primary.main",
                                fontWeight: 600,
                              },
                            },
                            "&:hover": { bgcolor: "action.hover" },
                          }}
                        >
                          <ListItemAvatar sx={{ minWidth: 40, mr: 1.5 }}>
                            <Avatar
                              sx={{
                                width: 36,
                                height: 36,
                                fontSize: "0.9rem",
                                bgcolor: isSelected
                                  ? "primary.main"
                                  : "grey.300",
                                color: isSelected ? "#fff" : "grey.600",
                              }}
                            >
                              {(user?.name || "?").charAt(0).toUpperCase()}
                            </Avatar>
                          </ListItemAvatar>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant='body2'
                              noWrap
                              sx={{ fontWeight: isSelected ? 600 : 500 }}
                            >
                              {user?.name || "Unnamed"}
                            </Typography>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                              noWrap
                            >
                              {user?.email}
                            </Typography>
                          </Box>
                          {hasPerms && (
                            <Badge
                              overlap='circular'
                              anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                              }}
                              variant='dot'
                              color='success'
                              sx={{ ml: 1 }}
                            />
                          )}
                        </ListItem>
                      );
                    })
                  : filteredCompanies.map((company, index) => {
                      const cId = company.c_id;
                      const isSelected = selectedCompanyId === cId;
                      return (
                        <ListItem
                          key={cId || company.uen || index}
                          button
                          selected={isSelected}
                          onClick={() => handleCompanyClick(company)}
                          sx={{
                            borderRadius: 2,
                            mb: 0.5,
                            px: 2,
                            py: 1.5,
                            "&.Mui-selected": {
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              "&:hover": {
                                bgcolor: alpha(
                                  theme.palette.primary.main,
                                  0.12
                                ),
                              },
                              "& .MuiTypography-root": {
                                color: "primary.main",
                                fontWeight: 600,
                              },
                            },
                            "&:hover": { bgcolor: "action.hover" },
                          }}
                        >
                          <ListItemAvatar sx={{ minWidth: 40, mr: 1.5 }}>
                            <Avatar
                              sx={{
                                width: 36,
                                height: 36,
                                fontSize: "0.9rem",
                                bgcolor: isSelected
                                  ? "primary.main"
                                  : "grey.300",
                                color: isSelected ? "#fff" : "grey.600",
                              }}
                            >
                              {(company?.name || company?.company_name || "?")
                                .charAt(0)
                                .toUpperCase()}
                            </Avatar>
                          </ListItemAvatar>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant='body2'
                              noWrap
                              sx={{ fontWeight: isSelected ? 600 : 500 }}
                            >
                              {company?.name ||
                                company?.company_name ||
                                "Company"}
                            </Typography>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                              noWrap
                            >
                              {company?.uen || company?.company_uen}
                            </Typography>
                            {company.isRestricted && <RestrictedChip />}
                          </Box>
                        </ListItem>
                      );
                    })}

                {viewMode === "user" && filteredUsers.length === 0 && (
                  <Box
                    sx={{ p: 3, textAlign: "center", color: "text.secondary" }}
                  >
                    <Typography variant='caption'>No users found</Typography>
                  </Box>
                )}

                {viewMode === "company" && filteredCompanies.length === 0 && (
                  <Box
                    sx={{ p: 3, textAlign: "center", color: "text.secondary" }}
                  >
                    <Typography variant='caption'>
                      No companies found
                    </Typography>
                  </Box>
                )}
              </List>

              {/* View permission table - bottom of left panel */}
              <Box
                sx={{
                  p: 2,
                  borderTop: 1,
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                <Button
                  fullWidth
                  size='small'
                  variant='outlined'
                  startIcon={<TableChartIcon />}
                  onClick={() => {
                    setTableModalOpen(true);
                    loadCompanyPermissionIndex();
                  }}
                  sx={{ textTransform: "none" }}
                >
                  View permission table
                </Button>
              </Box>
            </Box>

            {/* RIGHT: 权限详情 (Workspace) - 最大化内容区域 */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.default",
                overflow: "hidden",
              }}
            >
              {viewMode === "user" ? (
                <UserMode
                  selectedUserId={selectedUserId}
                  allUsers={allUsers}
                  handleImportPermissions={handleImportPermissions}
                  handleUpdatePermission={handleUpdatePermission}
                  handleSaveAllPermissions={handleSaveAllPermissions}
                  handleSavePermission={handleSavePermission}
                  handleSelectAllYears={handleSelectAllYears}
                  handleToggleYear={handleToggleYear}
                  getAvailableYears={getAvailableYears}
                  userPermissions={userPermissions}
                  loadingPermission={loadingPermission}
                  editingPermission={editingPermission}
                  savingPermission={savingPermission}
                  setEditingPermission={setEditingPermission}
                  setImportFromUserId={setImportFromUserId}
                  importFromUserId={importFromUserId}
                  importingPermission={importingPermission}
                  filteredUsers={filteredUsers}
                  selectedUserInactive={isUserInactive(selectedUserId)}
                />
              ) : (
                <CompanyMode
                  selectedCompanyId={selectedCompanyId}
                  companies={companies}
                  onSaveCompanyUserRoles={handleSaveCompanyUserRoles}
                  loadingCompanyPermissions={loadingCompanyPermissions}
                  companyPermissions={companyPermissions}
                  getAvailableYears={getAvailableYears}
                  getUserStatus={getUserStatus}
                  isUserInactive={isUserInactive}
                />
              )}
            </Box>
          </Box>

          {/* Global Notifications */}
          <Snackbar
            open={saveSuccess}
            autoHideDuration={3000}
            onClose={() => setSaveSuccess(false)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={() => setSaveSuccess(false)}
              severity='success'
              sx={{ width: "100%" }}
            >
              Permissions updated successfully
            </Alert>
          </Snackbar>
          <Snackbar
            open={Boolean(permissionBlockedMessage)}
            autoHideDuration={4000}
            onClose={() => setPermissionBlockedMessage("")}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={() => setPermissionBlockedMessage("")}
              severity='warning'
              sx={{ width: "100%" }}
            >
              {permissionBlockedMessage}
            </Alert>
          </Snackbar>
        </Box>
      </Modal>

      {/* Permission table dialog: focus trap, scroll lock, no backdrop close */}
      <Dialog
        open={tableModalOpen}
        onClose={(_, reason) => {
          if (reason !== "backdropClick") setTableModalOpen(false);
        }}
        maxWidth='lg'
        fullWidth
        scroll='body'
        disableEscapeKeyDown={false}
        PaperProps={{
          sx: {
            height: "85vh",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            borderRadius: 3,
            boxShadow: "0 24px 48px rgba(0,0,0,0.12)",
            overflow: "hidden",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          },
        }}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(0,0,0,0.25)",
          },
        }}
        sx={{ zIndex: 1400 }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant='h6' fontWeight={700} component='span'>
              Permission Table
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              View all permissions by company and user
            </Typography>
          </Box>
          <IconButton
            onClick={() => setTableModalOpen(false)}
            size='small'
            aria-label='Close'
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            p: 3,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <br />
          <PermissionTableView
            companies={companies}
            companyPermissions={companyPermissions}
            allUsers={allUsers}
            currentUserId={user_id}
            loadingCompanyPermissions={loadingCompanyPermissions}
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: 1,
            borderColor: "divider",
            gap: 1,
          }}
        >
          <Button
            size='small'
            startIcon={<RefreshIcon />}
            onClick={() => loadCompanyPermissionIndex()}
            disabled={loadingCompanyPermissions}
          >
            Refresh
          </Button>
          <Button
            size='small'
            variant='contained'
            onClick={() => setTableModalOpen(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManagerPanel;
